import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@evogard/ui';
import { CAF_WEBHOOK_KEY, canAccess } from '@evogard/domain';
import { api, errorMessage } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { SettingField } from './TenantSettingsScreen.js';

/**
 * Os templates da CAF (16/10/2026): o do formulário e o da consulta, para o
 * sinistro do associado e o do terceiro. Mudam no Trust da CAF, e por isso
 * ficam aqui, editáveis sem mexer no código.
 *
 * São chaves da Configuração da base (`caf.*`), com o mesmo campo e a mesma
 * validação de lá — e a mesma permissão, `platform.tenants:editar`, porque
 * valem para a praça inteira.
 */
export function CafTemplatesPanel({ tenantId }: { tenantId: number | undefined }) {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const canEdit = canAccess(context?.permissions ?? [], 'platform.tenants', 'editar');
  const key = ['plataforma', 'configuracao-da-base', tenantId];

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const r = await api.platform.listSetting();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar os templates.'));
      }
      return r.body.keys;
    },
    enabled: tenantId !== undefined,
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-texto-suave">
        O sinistro do associado usa o primeiro par, e o do terceiro o segundo. Os IDs
        estão no Trust da CAF: o do formulário em Onboarding Builder, e o da consulta em
        Query Templates.
      </p>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {query.data
        ?.filter((s) => s.key.startsWith('caf.') && s.key !== CAF_WEBHOOK_KEY)
        .map((setting) => (
          <SettingField
            key={setting.key}
            setting={setting}
            canEdit={canEdit}
            toWrite={(keys) => client.setQueryData(key, keys)}
          />
        ))}
      {!canEdit && (
        <p className="text-xs text-texto-suave">
          Trocar um template pede a permissão de editar a configuração da base.
        </p>
      )}
    </div>
  );
}

/**
 * O webhook da CAF (16/10/2026): o endereço que vai junto com cada link
 * gerado. Não há o que cadastrar no Trust — a CAF avisa onde o link mandou.
 *
 * Desde 19/10/2026 a base pode escrever outro (`caf.webhook`): só a origem de
 * um túnel, que continua avisando aqui, ou um endereço inteiro de outro
 * sistema, e aí a tela diz que o resultado não volta. O que aparece no bloco
 * é o que vai de fato nos links — vale para os gerados DEPOIS de salvar.
 */
export function CafWebhookPanel({ tenantId }: { tenantId: number | undefined }) {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const canEdit = canAccess(context?.permissions ?? [], 'platform.tenants', 'editar');
  const settingsKey = ['plataforma', 'configuracao-da-base', tenantId];
  const webhookKey = ['plataforma', 'integracoes', 'caf', 'webhook', tenantId];

  const query = useQuery({
    queryKey: webhookKey,
    queryFn: async () => {
      const r = await api.cafIntegration.getCafWebhook();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar o webhook.'));
      }
      return r.body;
    },
    enabled: tenantId !== undefined,
  });

  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: async () => {
      const r = await api.platform.listSetting();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar o endereço.'));
      }
      return r.body.keys;
    },
    enabled: tenantId !== undefined,
  });
  const setting = settings.data?.find((s) => s.key === CAF_WEBHOOK_KEY);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-texto-suave">
        Cada link gerado pela plataforma leva este endereço: é por ele que a CAF avisa
        quando o associado termina o envio e quando sai o resultado. Não é preciso
        cadastrá-lo no Trust — se preferir cadastrar lá também, é este o endereço.
      </p>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {query.data && (
        <>
          <code className="break-all rounded-borda bg-superficie-suave px-3 py-2 font-mono text-xs text-texto">
            {query.data.url}
          </code>
          {!query.data.reachable && (
            <Alert tone="alerta" title="A CAF não alcança este endereço">
              Ele aponta para esta máquina. Em desenvolvimento, use um túnel público e
              informe a origem dele abaixo; em produção, o endereço é o do servidor.
            </Alert>
          )}
          {!query.data.receivesHere && (
            <Alert tone="alerta" title="O resultado não volta para a plataforma">
              Este endereço é de outro sistema: a CAF avisa lá, e o sinistro não recebe a
              situação nem os documentos. Para voltar ao da plataforma, deixe o campo
              vazio.
            </Alert>
          )}
        </>
      )}
      {settings.isError && <Alert tone="erro">{settings.error.message}</Alert>}
      {setting && (
        <SettingField
          setting={setting}
          canEdit={canEdit}
          toWrite={(keys) => {
            client.setQueryData(settingsKey, keys);
            void client.invalidateQueries({ queryKey: webhookKey });
          }}
        />
      )}
      {!canEdit && (
        <p className="text-xs text-texto-suave">
          Trocar o endereço pede a permissão de editar a configuração da base.
        </p>
      )}
    </div>
  );
}
